import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

import { IAppointment } from '../../models/appointment.interface';
import { CVA_AUTH_MODULES } from '../../models/auth.modules.enum';
import { ROUTE_CONSTANTS } from '../../util/route.constants';
import { environment } from '../../../environments/environment';
import { VIEW_MODE, TAB, CVA_CONSTANTS } from '../../util/constants';
import { SoapServiceService } from '../../services/soap-service/soap-service.service';
import { ClassificationService } from '../../services/classification/classification.service';
import { PatientService } from '../../services/patient/patient.service';

enum ConsultationTopTab {
  Consultation = 'consultation',
  PatientSummary = 'patient-dashboard',
  ConsultationSummary = 'consultation-summary',
  Appointments = 'appointments'
}
@Component({
    standalone: false,
    selector: 'cva-consultation',
    templateUrl: './consultation.component.html',
    styleUrl: './consultation.component.scss',
    
})
export class ConsultationComponent implements OnDestroy {
  VIEW_MODE = VIEW_MODE;
  CVA_CONSTANTS = CVA_CONSTANTS;
  CVA_AUTH_MODULES = CVA_AUTH_MODULES;
  PRIVACY_POLICY_ROUTE = ROUTE_CONSTANTS.PRIVACY_POLICY;
  TUTORIAL_VIDEO_LINK = ROUTE_CONSTANTS.TUTORIAL_LINK;
  readonly patientDashboardRoute = `/${ROUTE_CONSTANTS.PATIENT_DASHBOARD}`;
  isLIC: string | null;

  appointment!: IAppointment;
  cvaUI_url = environment.cvaUI_url;
  viewMode!: VIEW_MODE;
  selectedTab: number = TAB.TRANSCRIPTION;
  reaload: boolean = false;
  patientSummaryRefreshToken = 0;
  consultationSummaryRefreshToken = 0;
  activeTopTab: ConsultationTopTab = ConsultationTopTab.Consultation;
  isConsultationRecording = false;
  hasPatientSummaryOpened = false;
  hasConsultationSummaryOpened = false;
  private patientSummaryNeedsRefresh = false;
  private consultationSummaryNeedsRefresh = false;
  topTab = ConsultationTopTab;

  private readonly destroy$ = new Subject<void>();

  get isLicUser(): boolean {
    return this.isLIC === 'true';
  }

  constructor(
    private readonly router: Router,
    private readonly soapServiceService: SoapServiceService,
    private readonly classificationService: ClassificationService,
    private readonly patientService: PatientService,
  ) {
    console.log("Consultation screen loaded");
    this.validateAndSaveAppointment();
    this.listenVitalsUpdated();
    this.listenClassificationChanges();
  }

  validateAndSaveAppointment() {
    this.isLIC = localStorage.getItem('isLIC');
    if (history?.state?.appointment) {
      this.viewMode = history.state.viewMode;
      this.appointment = history.state?.appointment as IAppointment;
      this.loadPatientContact();
      this.prepareConsultationState();
      this.reaload = history.state?.reaload ?? false;
      localStorage.setItem('patientId', this.appointment.patientId);
      if (this.isLIC == 'true') {
        this.soapServiceService.setCurrentAppointment(this.appointment);
      }
    } else {
      this.router.navigate([ROUTE_CONSTANTS.PATIENT_DASHBOARD]);
    }
  }

  private prepareConsultationState(): void {
    const isFromPatientSearch = !!history?.state?.appointment?.isFromPatientSearch;
    if (isFromPatientSearch) {
      localStorage.removeItem('tabs');
      localStorage.removeItem('consultationForm');
      localStorage.removeItem('disableAllApproves');
    }
  }

  private loadPatientContact(): void {
    const patient = this.appointment.patient;
    // Navigation state can contain an appointment loaded before phone was included.
    if (patient.phone?.trim() || !patient.medicalRecordNumber) {
      return;
    }
    this.patientService.getPatientById(patient.medicalRecordNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ response }) => {
          if (response?.phone) {
            this.appointment = {
              ...this.appointment,
              patient: { ...this.appointment.patient, phone: response.phone },
            };
          }
        },
        error: () => {
          // Keep the consultation usable when contact details cannot be fetched.
        },
      });
  }

  setActiveTopTab(tab: ConsultationTopTab): void {
    if (this.isLicUser && tab === ConsultationTopTab.Appointments) {
      return;
    }

    this.activeTopTab = tab;
    if (tab === ConsultationTopTab.PatientSummary) {
      if (this.hasPatientSummaryOpened && this.patientSummaryNeedsRefresh) {
        this.patientSummaryRefreshToken++;
      }
      this.patientSummaryNeedsRefresh = false;
      this.hasPatientSummaryOpened = true;
      return;
    }

    if (tab === ConsultationTopTab.ConsultationSummary) {
      if (this.hasConsultationSummaryOpened && this.consultationSummaryNeedsRefresh) {
        this.consultationSummaryRefreshToken++;
      }
      this.consultationSummaryNeedsRefresh = false;
      this.hasConsultationSummaryOpened = true;
    }
  }

  onConsultationNotesApproved(): void {
    this.patientSummaryNeedsRefresh = true;
  }

  onConsultationRecordingStateChange(isRecording: boolean): void {
    this.isConsultationRecording = isRecording;
  }

  switchToTabMode() {
    console.log("Switching to tab mode on consultation screen");
    this.selectedTab = TAB.TRANSCRIPTION;
    this.viewMode = VIEW_MODE.TAB;
  }


  gotoTemplateManagement() {
    if (this.isLIC) {
      this.soapServiceService.openWebPortal(ROUTE_CONSTANTS.SOAP_CONFIGURATION);
    }
  }

  openTutorialVideo() {
    window.open(`${this.cvaUI_url}/${this.TUTORIAL_VIDEO_LINK}`, '_blank');
  }

  private listenVitalsUpdated(): void {
    this.soapServiceService.vitalsUpdated$
      .pipe(
        filter((update) => update.appointmentRefId === this.appointment?.referenceId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.patientSummaryNeedsRefresh = true;
      });
  }

  private listenClassificationChanges(): void {
    this.classificationService.classificationChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.patientSummaryNeedsRefresh = true;
        this.consultationSummaryNeedsRefresh = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.isConsultationRecording = false;
    localStorage.removeItem('patientId');
    localStorage.removeItem('patientConsent');
  }
}
